import React, { useEffect, useState } from "react";
import { View, StyleSheet, Platform, useWindowDimensions } from "react-native";
import Animated, { useSharedValue, useAnimatedStyle, withSpring, runOnJS } from "react-native-reanimated";
import { Gesture, GestureDetector, Pressable } from "react-native-gesture-handler";
import { useCall } from "@/hooks/useCall";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { formatPhoneNumber } from "@/lib/phone";

const PILL_WIDTH = 240;
const PILL_HEIGHT = 64;

export function CallMinimized() {
  const { callState, currentCall, toggleMinimize, endCall, minimizedPosition, setMinimizedPosition } = useCall();
  const { colors } = useTheme();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const [duration, setDuration] = useState(0);

  // Initialize position from saved state or default to bottom-right
  const initialX = minimizedPosition?.x ?? screenWidth - PILL_WIDTH - 20;
  const initialY = minimizedPosition?.y ?? screenHeight - PILL_HEIGHT - 80;

  const translateX = useSharedValue(initialX);
  const translateY = useSharedValue(initialY);
  const startX = useSharedValue(0);
  const startY = useSharedValue(0);

  // Timer for active calls - called unconditionally
  useEffect(() => {
    if (currentCall?.startTime) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - currentCall.startTime!.getTime()) / 1000);
        setDuration(elapsed);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentCall?.startTime]);

  // Pan gesture for dragging
  const panGesture = Gesture.Pan()
    .onStart(() => {
      "worklet";
      startX.value = translateX.value;
      startY.value = translateY.value;
    })
    .onChange((event) => {
      "worklet";
      translateX.value = startX.value + event.translationX;
      translateY.value = startY.value + event.translationY;
    })
    .onEnd(() => {
      "worklet";
      // Clamp position to viewport bounds
      const clampedX = Math.max(0, Math.min(translateX.value, screenWidth - PILL_WIDTH));
      const clampedY = Math.max(0, Math.min(translateY.value, screenHeight - PILL_HEIGHT));

      translateX.value = withSpring(clampedX);
      translateY.value = withSpring(clampedY);

      // Save position to storage after animation completes
      runOnJS(setMinimizedPosition)({ x: clampedX, y: clampedY });
    });

  // Animated style - must be called unconditionally before early return
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }, { translateY: translateY.value }],
  }));

  // Early return after all hooks
  if (!currentCall || Platform.OS !== "web") return null;

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusText = () => {
    if (callState === "incoming") return "Incoming call...";
    if (callState === "outgoing") return "Calling...";
    return formatDuration(duration);
  };

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View
        style={[
          // styles.container uses web-only CSS (boxShadow, cursor) not modeled by ViewStyle
          styles.container as any,
          {
            backgroundColor: colors.card,
            borderColor: colors.border,
          },
          animatedStyle,
        ]}
      >
        <Pressable style={styles.content} onPress={toggleMinimize}>
          {currentCall.callerUserId ? (
            <Avatar userId={currentCall.callerUserId} size={40} />
          ) : (
            <Avatar
              firstName={currentCall.callerName.split(" ")[0]}
              lastName={currentCall.callerName.split(" ").slice(1).join(" ")}
              size={40}
            />
          )}

          <View style={styles.info}>
            <ThemedText style={styles.name} numberOfLines={1}>
              {currentCall.callerName}
            </ThemedText>
            <ThemedText style={[styles.duration, { color: colors.textSecondary }]}>
              {getStatusText()} · {formatPhoneNumber(currentCall.fromNumber)}
            </ThemedText>
          </View>

          <Pressable
            style={[styles.endButton, { backgroundColor: "#ef4444" }]}
            onPress={() => {
              endCall();
            }}
          >
            <MaterialCommunityIcons name="phone-hangup" size={18} color="#fff" />
          </Pressable>
        </Pressable>
      </Animated.View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    width: PILL_WIDTH,
    height: PILL_HEIGHT,
    borderRadius: 32,
    borderWidth: 1,
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
    //@ts-ignore
    cursor: "move",
  },
  content: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    gap: 12,
  },
  info: {
    flex: 1,
  },
  name: {
    fontSize: 14,
    fontWeight: "600",
  },
  duration: {
    fontSize: 12,
  },
  endButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
});
